import { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography, useTheme, Divider } from '@mui/material';
import { usePlanificadorCentro } from '../../api/aulas';
import type { PlanificadorData } from '../../api/aulas';
import { AulaCapacityChip } from './AulaCapacityChip';

function toMin(h: string) {
  const [H, M, S] = h.split(':').map(Number);
  return H * 60 + (M || 0) + (S ? S / 60 : 0);
}
function pct(h: string, start = '08:00', end = '22:00') {
  const total = toMin(end) - toMin(start);
  return ((toMin(h) - toMin(start)) / total) * 100;
}
function sameDow(fecha: string, dow: number) {
  return new Date(`${fecha}T00:00:00`).getDay() === dow;
}
function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}
function overlapMinutes(aStart: string, aEnd: string, bStart: string, bEnd: string) {
  const s = Math.max(toMin(aStart), toMin(bStart));
  const e = Math.min(toMin(aEnd), toMin(bEnd));
  return Math.max(0, e - s);
}

type Props = {
  centroId: number;
  fecha: string;
  aulaId?: number;
  dayStart?: string; // HH:mm
  dayEnd?: string; // HH:mm
};

const TRACK_H = 42;
const BLOCK_H = 10;

export function AulaCalendarDay({
  centroId,
  fecha,
  aulaId,
  dayStart = '08:00',
  dayEnd = '22:00',
}: Props) {
  const theme = useTheme();
  const { data, isLoading } = usePlanificadorCentro(centroId, fecha);

  const aulas = useMemo(() => {
    if (!data) return [] as NonNullable<PlanificadorData['aulas']>;
    return aulaId ? data.aulas.filter((a) => a.id === aulaId) : data.aulas;
  }, [data, aulaId]);

  const hours = useMemo(() => {
    const start = toMin(dayStart);
    const end = toMin(dayEnd);
    const list: { label: string; left: number; m: number }[] = [];
    for (let m = start; m <= end; m += 60) {
      const H = Math.floor(m / 60);
      const M = m % 60;
      const label = `${String(H).padStart(2, '0')}:${String(M).padStart(2, '0')}`;
      list.push({ label, left: ((m - start) / (end - start)) * 100, m });
    }
    return list;
  }, [dayStart, dayEnd]);

  const isToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return today === fecha;
  }, [fecha]);

  if (isLoading || !data) {
    return <Typography> Cargando calendario… </Typography>;
  }

  const ScaleHeader = (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        mb: 1,
      }}
    >
      <Box sx={{ position: 'relative', height: 24 }}>
        {hours.map((h, i) => (
          <Box key={i} sx={{ position: 'absolute', left: `${h.left}%`, top: 0, bottom: 0 }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 1,
                bgcolor: theme.palette.divider,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: -8,
                transform: 'translateX(-50%)',
                bgcolor: 'background.paper',
                px: 0.5,
              }}
            >
              {h.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const Legend = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
      <Chip
        size="small"
        label="Asignada"
        sx={{
          bgcolor: theme.palette.primary.light,
          color: theme.palette.getContrastText(theme.palette.primary.light),
        }}
      />
      <Chip
        size="small"
        label="Pendiente"
        sx={{
          bgcolor: theme.palette.warning.light,
          color: theme.palette.getContrastText(theme.palette.warning.light),
        }}
      />
      <Chip
        size="small"
        label="Bloqueo"
        sx={{
          bgcolor: theme.palette.error.light,
          color: theme.palette.getContrastText(theme.palette.error.light),
        }}
      />
    </Stack>
  );

  const prioridadChip = (p?: 'ALTA' | 'MEDIA' | 'BAJA') => (
    <Chip
      size="small"
      variant="outlined"
      label={`Prioridad ${p ?? 'MEDIA'}`}
      sx={{ textTransform: 'capitalize' }}
    />
  );

  return (
    <Stack spacing={1}>
      {ScaleHeader}

      <Stack spacing={2}>
        {aulas.map((aula) => {
          const reservas = data.reservas.filter((r) => r.id_aula === aula.id);
          const bloqueos = data.bloqueos.filter(
            (b) =>
              b.id_aula === aula.id &&
              b.activo &&
              sameDow(fecha, b.dow) &&
              fecha >= b.fecha_inicio &&
              fecha <= b.fecha_fin,
          );

          const totalMin = toMin(dayEnd) - toMin(dayStart);
          const ocupMin = reservas
            .filter((r) => r.aula_estado === 'ASIGNADA')
            .reduce(
              (acc, r) => acc + overlapMinutes(dayStart, dayEnd, r.hora_inicio, r.hora_fin),
              0,
            );
          const ocupPct = Math.round(100 * clamp01(ocupMin / totalMin));

          return (
            <Paper key={aula.id} variant="outlined" sx={{ p: 1.25 }}>
              {/* Cabecera de aula */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {aula.nombre}
                  </Typography>
                  <AulaCapacityChip
                    capacidad={aula.capacidad}
                    cat={(aula as any).capacidad_cat ?? 'S'}
                  />
                  {/* ← NUEVO: mostrar prioridad */}
                  {prioridadChip((aula as any).prioridad)}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Uso ${(aula as any).uso_aula ?? 'MIXTA'}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Internet ${(aula as any).internet_perfil ?? 'BASICO'}`}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" variant="outlined" label={fecha} />
                  <Chip
                    size="small"
                    label={`Ocupación ${ocupPct}%`}
                    sx={{
                      bgcolor:
                        ocupPct >= 85
                          ? theme.palette.error.light
                          : ocupPct >= 60
                            ? theme.palette.warning.light
                            : theme.palette.success.light,
                      color: theme.palette.getContrastText(
                        ocupPct >= 85
                          ? theme.palette.error.light
                          : ocupPct >= 60
                            ? theme.palette.warning.light
                            : theme.palette.success.light,
                      ),
                    }}
                  />
                </Stack>
              </Stack>

              {/* Timeline por aula */}
              <Box
                sx={{
                  position: 'relative',
                  height: TRACK_H + BLOCK_H + 8,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                {/* Rejilla vertical (líneas) */}
                {hours.map((h, i) => (
                  <Box
                    key={`grid-${i}`}
                    sx={{
                      position: 'absolute',
                      left: `${h.left}%`,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      bgcolor: theme.palette.divider,
                      opacity: 0.15,
                      zIndex: 1,
                    }}
                  />
                ))}

                {/* ⬇️ Etiquetas de horas dentro de la barra */}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 2,
                    height: 14,
                    pointerEvents: 'none',
                    zIndex: 2, // por encima de la rejilla, por debajo de eventos
                  }}
                >
                  {hours.map((h, i) => (
                    <Typography
                      key={`hlabel-${i}`}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${h.left}%`,
                        transform:
                          i === hours.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                        color: 'text.secondary',
                        opacity: 0.7,
                        fontSize: 11,
                        lineHeight: '14px',
                      }}
                    >
                      {h.label}
                    </Typography>
                  ))}
                </Box>

                {/* Línea de la hora actual */}
                {isToday &&
                  (() => {
                    const now = new Date();
                    const hh = String(now.getHours()).padStart(2, '0');
                    const mm = String(now.getMinutes()).padStart(2, '0');
                    const nowStr = `${hh}:${mm}`;
                    const left = pct(nowStr, dayStart, dayEnd);
                    if (left >= 0 && left <= 100) {
                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${left}%`,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            bgcolor: theme.palette.info.main,
                            opacity: 0.6,
                            zIndex: 5,
                          }}
                        />
                      );
                    }
                    return null;
                  })()}

                {/* Reservas */}
                {reservas.map((r) => {
                  const left = pct(r.hora_inicio, dayStart, dayEnd);
                  const width = pct(r.hora_fin, dayStart, dayEnd) - left;

                  const assigned = r.aula_estado === 'ASIGNADA';
                  const bg = assigned ? theme.palette.primary.light : theme.palette.warning.light;

                  const label = [
                    `R#${r.id}`,
                    `${r.hora_inicio.slice(0, 5)}–${r.hora_fin.slice(0, 5)}`,
                    `${r.num_alumnos} alumno${(r.num_alumnos ?? 1) === 1 ? '' : 's'}`,
                    r.aula_estado ?? '—',
                  ].join(' · ');

                  return (
                    <Tooltip
                      key={`r-${r.id}`}
                      title={
                        <Stack spacing={0.25} sx={{ p: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >{`Reserva #${r.id}`}</Typography>
                          <Typography variant="caption">{`Horario: ${r.hora_inicio} – ${r.hora_fin}`}</Typography>
                          <Typography variant="caption">{`Alumnos: ${r.num_alumnos}`}</Typography>
                          <Typography variant="caption">{`Estado: ${r.aula_estado}`}</Typography>
                        </Stack>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(0, width)}%`,
                          top: 6 + 14, // deja hueco visual bajo las etiquetas
                          height: TRACK_H - 12 - 14,
                          bgcolor: bg,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          boxShadow: theme.shadows[1],
                          border: '1px solid',
                          borderColor: 'divider',
                          fontSize: 13,
                          fontWeight: 500,
                          zIndex: 4,
                        }}
                      >
                        {label}
                      </Box>
                    </Tooltip>
                  );
                })}

                {/* Bloqueos (franja inferior) */}
                {bloqueos.map((b) => {
                  const left = pct(b.hora_inicio, dayStart, dayEnd);
                  const width = pct(b.hora_fin, dayStart, dayEnd) - left;
                  const label = `Bloqueo • ${b.hora_inicio.slice(0, 5)}–${b.hora_fin.slice(0, 5)}${b.motivo ? ` · ${b.motivo}` : ''}`;

                  return (
                    <Tooltip key={`b-${b.id}`} title={label} arrow placement="bottom">
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(0, width)}%`,
                          bottom: 6,
                          height: BLOCK_H,
                          bgcolor: theme.palette.error.light,
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: theme.palette.error.main,
                          opacity: 0.9,
                          zIndex: 3,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Divider sx={{ mt: 1 }} />
      {Legend}
    </Stack>
  );
}
